import React, { useState } from 'react';
import { Search, MapPin } from 'lucide-react';
import { useDebounce } from '../../../hooks/useDebounce';
import { searchAddress } from '../../../services/addressService';

interface LocationInputProps {
  value: {
    postcode: string;
    city: string;
    address: string;
  };
  onChange: (location: { postcode: string; city: string; address: string }) => void;
}

const INPUT: React.CSSProperties = {
  border: '0.5px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: '10px 14px',
  fontSize: 13,
  color: '#1E1148',
  width: '100%',
  outline: 'none',
  background: 'white',
};

const LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: '#7C6FA0',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 6,
  display: 'block',
};

const LocationInput: React.FC<LocationInputProps> = ({ value, onChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Array<{
    address: string;
    postcode: string;
    city: string;
  }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);

  const debouncedQuery = useDebounce(searchQuery, 300);

  React.useEffect(() => {
    const fetchSuggestions = async () => {
      if (debouncedQuery.length >= 3 && !hasSelected) {
        const results = await searchAddress(debouncedQuery);
        setSuggestions(results);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };
    fetchSuggestions();
  }, [debouncedQuery, hasSelected]);

  const handleSelectAddress = (suggestion: typeof suggestions[0]) => {
    onChange(suggestion);
    setSearchQuery(suggestion.address);
    setShowSuggestions(false);
    setHasSelected(true);
    setIsFocused(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setHasSelected(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Address search */}
      <div>
        <label style={LABEL}>Recherche d'adresse</label>
        <div style={{ position: 'relative' }}>
          <Search
            size={14}
            color="#B8B0CC"
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            placeholder="Ex : 12 rue de la Paix, Paris"
            style={{ ...INPUT, paddingLeft: 34 }}
          />
          {showSuggestions && suggestions.length > 0 && isFocused && (
            <div style={{
              position: 'absolute', zIndex: 20, width: '100%', marginTop: 4,
              background: 'white', borderRadius: 'var(--radius-md)',
              border: '0.5px solid var(--color-border)',
              boxShadow: 'var(--shadow-card)',
              overflow: 'hidden',
            }}>
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  style={{
                    width: '100%', padding: '10px 14px', textAlign: 'left',
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    background: 'none', border: 'none', cursor: 'pointer',
                    borderBottom: index < suggestions.length - 1 ? '0.5px solid var(--color-border)' : 'none',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#F8F7FF')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                  onClick={() => handleSelectAddress(suggestion)}
                >
                  <MapPin size={13} color="#B8B0CC" style={{ marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 13, color: '#1E1148', fontWeight: 500 }}>{suggestion.address}</div>
                    <div style={{ fontSize: 11, color: '#7C6FA0', marginTop: 2 }}>{suggestion.postcode} {suggestion.city}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Postcode + City */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={LABEL}>Code postal</label>
          <input
            type="text"
            value={value.postcode}
            onChange={(e) => onChange({ ...value, postcode: e.target.value })}
            placeholder="75001"
            style={INPUT}
            required
          />
        </div>
        <div>
          <label style={LABEL}>Ville</label>
          <input
            type="text"
            value={value.city}
            onChange={(e) => onChange({ ...value, city: e.target.value })}
            placeholder="Paris"
            style={INPUT}
            required
          />
        </div>
      </div>

      {/* Street address */}
      <div>
        <label style={LABEL}>Adresse</label>
        <input
          type="text"
          value={value.address}
          onChange={(e) => onChange({ ...value, address: e.target.value })}
          placeholder="Numéro et nom de la rue"
          style={INPUT}
          required
        />
      </div>
    </div>
  );
};

export default LocationInput;
